<template>
  <div class="navigator-container">
    <div class="navigator-content">
      <b-button
        class="prev-btn"
        @click="previousStep"
        v-if="prevVisible && !withoutButtons"
      >
        <b-icon
          class="prev-btn--icon"
          icon-pack="fas"
          icon="angle-left"
        ></b-icon>

        <span class="prev-btn--label">{{
          $t("mp:flow_navigator.button.previous_step")
        }}</span>
      </b-button>

      <div class="progress-bar">
        <p class="is-size-7 has-text-centered">
          <span class="has-text-weight-medium"
            >{{ currentStepIndex + 1 + stepCorrection }}/{{ totalSteps }}
            {{ $t("mp:flow_navigator.label.step") }}</span
          >
          <span class="has-text-grey-icon ml-1" v-if="remainingMinutes > 0"
            >({{ remainingMinutes }}
            {{ $t("mp:flow_navigator.label.minute") }})</span
          >
        </p>
        <b-progress
          type="is-green"
          :value="progress"
          size="is-small"
        ></b-progress>
      </div>

      <b-button
        v-if="nextVisible && !withoutButtons"
        class="next-btn"
        type="is-blue"
        @click="nextStep"
        >{{ $t("mp:flow_navigator.button.next_step") }}</b-button
      >
    </div>
  </div>
</template>

<script>
import { FLOW_POSITION } from "../../utils/const";
import { mapActions } from "vuex";
import i18n from "@/plugins/i18n";
import eventBus, { EVENTS } from "@/utils/eventBus";
import { nextStepConfigOverride } from "../../utils/util";

export default {
  name: "ApplicationFlowNavigator",
  props: {
    applicationId: {
      type: Number,
      required: true,
    },
    steps: {
      type: Array,
      required: true,
    },
    currentStepIndex: {
      type: Number,
      required: true,
    },
    flowPosition: {
      type: String,
      required: true,
    },
    withoutButtons: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    stepCorrection() {
      let flowConfig = this.$store.getters["application/flowConfig"];
      let correction = 0;
      switch (this.flowPosition) {
        case FLOW_POSITION.FLOW:
          correction = 0;
          break;
        case FLOW_POSITION.OVERVIEW:
          correction = 1;
          break;
        case FLOW_POSITION.DATA_VALIDATION:
          correction = 2;
          break;
        case FLOW_POSITION.KYC_PREPARATION:
        case FLOW_POSITION.KYC_CHOOSE:
        case FLOW_POSITION.KYC_WAITING:
          correction = 3;
          if (!flowConfig.dataValidationRequired) {
            correction--;
          }
          break;
        case FLOW_POSITION.CONTRACT_CHECK:
          correction = 4;
          if (!flowConfig.dataValidationRequired) {
            correction--;
          }
          if (!flowConfig.identificationRequired) {
            correction--;
          }
          break;
        case FLOW_POSITION.SIGN_CHOOSE:
        case FLOW_POSITION.SIGN_PREPARATION:
        case FLOW_POSITION.SIGN_WAITING:
          correction = 5;
          if (!flowConfig.dataValidationRequired) {
            correction--;
          }
          if (!flowConfig.identificationRequired) {
            correction--;
          }
          if (!flowConfig.contractCheckRequired) {
            correction--;
          }
          break;
        case FLOW_POSITION.RESULT:
          correction = 6;
          if (!flowConfig.dataValidationRequired) {
            correction--;
          }
          if (!flowConfig.identificationRequired) {
            correction--;
          }
          if (!flowConfig.contractCheckRequired) {
            correction--;
          }
          if (!flowConfig.signatureRequired) {
            correction--;
          }
          break;
      }
      return correction;
    },
    nextVisible() {
      if (
        this.flowPosition === FLOW_POSITION.DATA_VALIDATION ||
        this.flowPosition === FLOW_POSITION.KYC_WAITING ||
        this.flowPosition === FLOW_POSITION.KYC_CHOOSE ||
        this.flowPosition === FLOW_POSITION.SIGN_WAITING ||
        this.flowPosition === FLOW_POSITION.SIGN_CHOOSE ||
        this.flowPosition === FLOW_POSITION.CONTRACT_CHECK ||
        this.flowPosition === FLOW_POSITION.RESULT
      )
        return false;
      return true;
    },
    prevVisible() {
      if (this.flowPosition === FLOW_POSITION.FLOW)
        return this.currentStepIndex !== 0;
      if (
        this.flowPosition === FLOW_POSITION.OVERVIEW ||
        this.flowPosition === FLOW_POSITION.KYC_CHOOSE
      )
        return true;
      if (this.flowPosition === FLOW_POSITION.SIGN_CHOOSE) return true;
      return false;
    },
    totalSteps() {
      let steps = this.steps.length + 2;
      let flowConfig = this.$store.getters["application/flowConfig"];
      if (flowConfig.dataValidationRequired) {
        steps++;
      }
      if (flowConfig.identificationRequired) {
        steps++;
      }
      if (flowConfig.contractCheckRequired) {
        steps++;
      }
      if (flowConfig.signatureRequired) {
        steps++;
      }
      return steps;
    },
    progress() {
      if (
        this.flowPosition === FLOW_POSITION.FLOW ||
        this.flowPosition === FLOW_POSITION.OVERVIEW ||
        this.flowPosition === FLOW_POSITION.DATA_VALIDATION ||
        this.flowPosition === FLOW_POSITION.KYC_PREPARATION ||
        this.flowPosition === FLOW_POSITION.KYC_CHOOSE ||
        this.flowPosition === FLOW_POSITION.KYC_WAITING ||
        this.flowPosition === FLOW_POSITION.CONTRACT_CHECK ||
        this.flowPosition === FLOW_POSITION.SIGN_PREPARATION ||
        this.flowPosition === FLOW_POSITION.SIGN_CHOOSE ||
        this.flowPosition === FLOW_POSITION.SIGN_WAITING
      ) {
        return (
          ((this.currentStepIndex + this.stepCorrection + 1) /
            this.totalSteps) *
          100
        );
      }
      if (this.flowPosition === FLOW_POSITION.RESULT) {
        return 100;
      }
      return (
        (this.currentStepIndex + this.stepCorrection + 1) / this.totalSteps
      ); // default
    },
    totalSecondsNeeded() {
      let total = 600 + 300 + 1200 + 60;
      // kyc + contract + sign + a minute
      this.steps.forEach(
        (step) => (total += step.estimatedTimeToAnswerInSeconds)
      );

      return total;
    },
    remainingMinutes() {
      let elapsedSeconds = 0;
      for (let i = 0; i < this.currentStepIndex && i < this.steps.length; i++) {
        elapsedSeconds += this.steps[i].estimatedTimeToAnswerInSeconds;
      }
      if (
        this.flowPosition === FLOW_POSITION.CONTRACT_CHECK ||
        this.flowPosition === FLOW_POSITION.OVERVIEW ||
        this.flowPosition === FLOW_POSITION.DATA_VALIDATION
      ) {
        elapsedSeconds += 600;
      }
      if (
        this.flowPosition === FLOW_POSITION.SIGN_PREPARATION ||
        this.flowPosition === FLOW_POSITION.SIGN_CHOOSE ||
        this.flowPosition === FLOW_POSITION.SIGN_WAITING ||
        this.flowPosition === FLOW_POSITION.KYC_PREPARATION ||
        this.flowPosition === FLOW_POSITION.KYC_CHOOSE ||
        this.flowPosition === FLOW_POSITION.KYC_WAITING
      ) {
        elapsedSeconds += 600 + 300;
      }
      if (this.flowPosition === FLOW_POSITION.RESULT) {
        return 0;
      }
      return Math.floor((this.totalSecondsNeeded - elapsedSeconds) / 60);
    },
  },
  methods: {
    ...mapActions({
      savePosition: "application/savePosition",
    }),
    async nextStep() {
      let flowConfig = this.$store.getters["application/flowConfig"];

      if (this.flowPosition === FLOW_POSITION.FLOW) {
        eventBus.$emit(EVENTS.FLOW_VALIDATION);
        if (
          !this.$store.getters["application/canContinue"](this.currentStepIndex)
        ) {
          this.$buefy.toast.open({
            message: i18n.t(
              "mp:flow_navigator.message.validator.all_fields_required"
            ),
            type: "is-danger",
            position: "is-bottom",
          });
          return;
        }
        if (this.currentStepIndex === this.steps.length - 1) {
          await this.savePosition({
            id: this.applicationId,
            flowPosition: FLOW_POSITION.OVERVIEW,
            currentFlowStepIndex: this.currentStepIndex,
          });
        } else {
          await this.savePosition({
            id: this.applicationId,
            flowPosition: FLOW_POSITION.FLOW,
            currentFlowStepIndex: this.currentStepIndex + 1,
          });
        }
      } else if (this.flowPosition === FLOW_POSITION.INTRO) {
        await this.savePosition({
          id: this.applicationId,
          flowPosition: FLOW_POSITION.FLOW,
          currentFlowStepIndex: 0,
        });
      } else if (this.flowPosition === FLOW_POSITION.OVERVIEW) {
        await this.savePosition({
          id: this.applicationId,
          flowPosition: nextStepConfigOverride(
            flowConfig,
            FLOW_POSITION.OVERVIEW
          ),
          currentFlowStepIndex: this.currentStepIndex,
        });
      } else if (this.flowPosition === FLOW_POSITION.KYC_PREPARATION) {
        await this.savePosition({
          id: this.applicationId,
          flowPosition: FLOW_POSITION.KYC_CHOOSE,
          currentFlowStepIndex: this.currentStepIndex,
        });
      } else if (this.flowPosition === FLOW_POSITION.KYC_CHOOSE) {
        await this.savePosition({
          id: this.applicationId,
          flowPosition: FLOW_POSITION.KYC_WAITING,
          currentFlowStepIndex: this.currentStepIndex,
        });
      } else if (this.flowPosition === FLOW_POSITION.KYC_WAITING) {
        await this.savePosition({
          id: this.applicationId,
          flowPosition: FLOW_POSITION.CONTRACT_CHECK,
          currentFlowStepIndex: this.currentStepIndex,
        });
      } else if (this.flowPosition === FLOW_POSITION.CONTRACT_CHECK) {
        await this.savePosition({
          id: this.applicationId,
          flowPosition: FLOW_POSITION.SIGN_PREPARATION,
          currentFlowStepIndex: this.currentStepIndex,
        });
      } else if (this.flowPosition === FLOW_POSITION.SIGN_PREPARATION) {
        await this.savePosition({
          id: this.applicationId,
          flowPosition: FLOW_POSITION.SIGN_CHOOSE,
          currentFlowStepIndex: this.currentStepIndex,
        });
      } else if (this.flowPosition === FLOW_POSITION.SIGN_CHOOSE) {
        await this.savePosition({
          id: this.applicationId,
          flowPosition: FLOW_POSITION.SIGN_WAITING,
          currentFlowStepIndex: this.currentStepIndex,
        });
      } else if (this.flowPosition === FLOW_POSITION.SIGN_WAITING) {
        await this.savePosition({
          id: this.applicationId,
          flowPosition: FLOW_POSITION.RESULT,
          currentFlowStepIndex: this.currentStepIndex,
        });
      }

      await this.$store.dispatch("application/fetchApplication");
    },
    async previousStep() {
      if (this.flowPosition === FLOW_POSITION.FLOW) {
        if (this.currentStepIndex === 0) {
          await this.savePosition({
            id: this.applicationId,
            flowPosition: FLOW_POSITION.INTRO,
            currentFlowStepIndex: 0,
          });
        } else {
          await this.savePosition({
            id: this.applicationId,
            flowPosition: FLOW_POSITION.FLOW,
            currentFlowStepIndex: this.currentStepIndex - 1,
          });
        }
      } else if (this.flowPosition === FLOW_POSITION.INTRO) {
        // noop();
      } else if (this.flowPosition === FLOW_POSITION.OVERVIEW) {
        await this.savePosition({
          id: this.applicationId,
          flowPosition: FLOW_POSITION.FLOW,
          currentFlowStepIndex: this.currentStepIndex,
        });
      } else if (this.flowPosition === FLOW_POSITION.KYC_PREPARATION) {
        await this.savePosition({
          id: this.applicationId,
          flowPosition: FLOW_POSITION.OVERVIEW,
          currentFlowStepIndex: this.currentStepIndex,
        });
      } else if (this.flowPosition === FLOW_POSITION.KYC_CHOOSE) {
        await this.savePosition({
          id: this.applicationId,
          flowPosition: FLOW_POSITION.KYC_PREPARATION,
          currentFlowStepIndex: this.currentStepIndex,
        });
      } else if (this.flowPosition === FLOW_POSITION.KYC_WAITING) {
        // noop();
      } else if (this.flowPosition === FLOW_POSITION.CONTRACT_CHECK) {
        // noop();
      } else if (this.flowPosition === FLOW_POSITION.SIGN_PREPARATION) {
        await this.savePosition({
          id: this.applicationId,
          flowPosition: FLOW_POSITION.CONTRACT_CHECK,
          currentFlowStepIndex: this.currentStepIndex,
        });
      } else if (this.flowPosition === FLOW_POSITION.SIGN_CHOOSE) {
        await this.savePosition({
          id: this.applicationId,
          flowPosition: FLOW_POSITION.SIGN_PREPARATION,
          currentFlowStepIndex: this.currentStepIndex,
        });
      } else if (this.flowPosition === FLOW_POSITION.SIGN_WAITING) {
        // noop();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.navigator-container {
  background: white;
}
</style>
